import React, { useCallback } from 'react';
import { View, useWindowDimensions, ScrollView, Platform } from 'react-native';
import { Button, Card, IconButton, Portal, Dialog, Text, useTheme } from 'react-native-paper';
import { getFirestore, doc, setDoc, arrayRemove, arrayUnion } from 'firebase/firestore';
import dayjs from 'dayjs';
import Toast from 'react-native-root-toast';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import * as Clipboard from 'expo-clipboard';

import Global from '../constants/global';
import ScaledImage from './ScaledImage';
import DAvatar from './DAvatar';


const MediaItem = ({ item, width, height }) => {
    const url = item.content?.includes("https://") ? item.content : Global.getPicUrl(item.content);
    return (
        <View style={{ padding: 1 }}>
            <ScaledImage uri={url} width={width - 2} height={height - 2} />

            {(item.title || item.desc) && (
                <View style={{
                    padding: 5,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,   // 👈 anchor overlay to left
                    right: 0,
                    width: '100%'
                }}>
                    {item.title && <Text style={{ color: "white" }} variant="labelMedium">{item.title}</Text>}
                </View>
            )}
        </View>
    );
};

const PostRow = React.memo(({ row, login, navigation }) => {
    const [showDelete, setDelete] = React.useState(false);
    const [showGallery, setGallery] = React.useState(false);
    const { colors } = useTheme();
    const SCREEN_WIDTH = useWindowDimensions().width - 36;

    const ago = dayjs().diff(dayjs.unix(row.stamp), 'day');
    const isLiked = row.likes?.some(e => e.id === login?.id);

    const handleLike = useCallback(async () => {
        if (!login?.id) return navigation.navigate("Login");

        const db = getFirestore();
        const postRef = doc(db, "Posts", row.id);
        const userLikeObj = {
            id: login.id,
            name: login.name,
            dp: login.dp || "",
            stamp: dayjs().unix()
        };

        try {
            await setDoc(postRef, {
                likes: isLiked ? arrayRemove(row.likes.find(l => l.id === login.id)) : arrayUnion(userLikeObj)
            }, { merge: true });
        } catch (e) {
            Toast.show("Connection Error");
        }
    }, [row.id, login, isLiked]);

    const handleShare = async () => {
        if (!row.media?.[0]?.content) return;
        const imageUrl = Global.getPicUrl(row.media[0].content);

        try {
            if (Platform.OS !== 'web') {
                const fileUri = `${FileSystem.cacheDirectory}share_image.jpg`;
                await FileSystem.downloadAsync(imageUrl, fileUri);
                await Clipboard.setStringAsync(`*${row.title}*\n${row.desc}`);
                await Sharing.shareAsync(fileUri);
            } else {
                await Clipboard.setStringAsync(`${row.title}\n${row.desc}\n${imageUrl}`);
                Toast.show("Copied to clipboard for sharing");
            }
        } catch (e) {
            console.error(e);
        }
    };
    const isDesktop = Platform.OS === 'web' && SCREEN_WIDTH > 920;
    return (
        <Card style={{ marginLeft: 10, marginRight: 10, marginBottom: 20, borderRadius: 12 }} elevation={2}>
            {/* Header */}
            <View style={{ flexDirection: "row", alignItems: 'center', padding: 10 }}>
                <DAvatar title={row.postedBy?.name} source={row.postedBy?.pic} size={40} />
                <View style={{ marginLeft: 10, flex: 1 }}>
                    <Text variant="titleMedium">{row.title}</Text>
                    <Text variant="labelSmall" style={{ opacity: 0.7 }}>
                        By {row.postedBy?.name} • {ago === 0 ? 'Today' : `${ago} days ago`}
                    </Text>
                </View>
            </View>

            {/* Content */}
            <View style={{ paddingHorizontal: 15, paddingBottom: 10 }}>
                {row.desc &&
                    <Text
                        numberOfLines={isDesktop ? 4 : undefined}   // limit to 4 lines on desktop
                        ellipsizeMode="tail"
                        style={{ height: isDesktop ? 80 : undefined }}
                        variant="bodyMedium">{row.desc}</Text>}
                {row.date && <Text variant="labelSmall">Date: {dayjs.unix(row.date).format("DD MMM YYYY")}</Text>}
            </View>

            <MediaGrid width={SCREEN_WIDTH} media={row.media} isMobile={!isDesktop} />

            {/* Actions */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-around', borderTopWidth: 0.5, borderTopColor: '#eee', marginTop: 10 }}>
                <IconButton
                    icon={isLiked ? "thumb-up" : "thumb-up-outline"}
                    iconColor={isLiked ? colors.primary : colors.outline}
                    onPress={handleLike}
                />
                <IconButton
                    icon="comment-outline"
                    onPress={() => login?.id ? navigation.navigate("Comments", { entry: row, login }) : navigation.navigate("Login")}
                />
                <IconButton icon="share-variant-outline" onPress={handleShare} />

                {login?.id && (login.id === row.postedBy?.id || login.type === Global.PRINCIPAL) && (
                    <>
                        <IconButton icon="pencil-outline" onPress={() => navigation.navigate("NewPost", { entry: row })} />
                        <IconButton icon="trash-can-outline" onPress={() => setDelete(true)} />
                    </>
                )}

            </View>

            {/* Delete Dialog */}
            <Portal>
                <Dialog visible={showDelete} onDismiss={() => setDelete(false)}>
                    <Dialog.Title>Confirm Delete</Dialog.Title>
                    <Dialog.Content><Text>Are you sure you want to delete this post?</Text></Dialog.Content>
                    <Dialog.Actions>
                        <Button onPress={() => setDelete(false)}>Cancel</Button>
                        <Button textColor={colors.error} onPress={() => {
                            setDelete(false);
                            const db = getFirestore();
                            setDoc(doc(db, "Posts", row.id), { flag: -10 }, { merge: true });
                        }}>Delete</Button>
                    </Dialog.Actions>
                </Dialog>
            </Portal>


        </Card>
    );
});

export default PostRow;


const MediaGrid = ({ media, isMobile, width }) => {
    const maxMedia = media.slice(0, 6); // cap at 6

    if (!isMobile) {
        // desktop: just show carousel/gallery
        return (
            <ScrollView horizontal pagingEnabled showsHorizontalScrollIndicator={false}>
                {maxMedia.map((m, i) => (
                    <MediaItem key={i} item={m} width={width - 20} height={200} />
                ))}
            </ScrollView>
        );
    }

    // mobile layout rules
    const renderRow = (items, rowWidth) => (
        <View style={{ flexDirection: "row" }}>
            {items.map((m, i) => (
                <MediaItem key={i} item={m} width={rowWidth} height={150} />
            ))}
        </View>
    );

    switch (maxMedia.length) {
        case 1:
            return <MediaItem item={maxMedia[0]} width={width} height={200} />;
        case 2:
            return renderRow(maxMedia, (width) / 2);
        case 3:
            return (
                <View style={{ width: "100%" }}>
                    <MediaItem item={maxMedia[0]} width={width} height={200} />
                    {renderRow(maxMedia.slice(1), (width) / 2)}
                </View>
            );
        case 4:
            return (
                <View>
                    {renderRow(maxMedia.slice(0, 2), (width) / 2)}
                    {renderRow(maxMedia.slice(2), (width) / 2)}
                </View>
            );
        case 5:
            return (
                <View>
                    <MediaItem item={maxMedia[0]} width={width} height={200} />
                    {renderRow(maxMedia.slice(1, 3), (width) / 2)}
                    {renderRow(maxMedia.slice(3), (width) / 2)}
                </View>
            );
        case 6:
            return (
                <View>
                    {renderRow(maxMedia.slice(0, 2), (width) / 2)}
                    {renderRow(maxMedia.slice(2, 4), (width) / 2)}
                    {renderRow(maxMedia.slice(4), (width) / 2)}
                </View>
            );
        default:
            return null;
    }
};